import type { Step2Result } from "./CreateShopTierBenefitStep/Step2";
import type { EditableTierBenefit } from "./types";
import { toRebatePercent } from "./rebatePercent";
import {
  TierAuditBefore,
  TierAuditDiff,
  TierAuditLevelChange,
  TierAuditLevelGroup,
  TierAuditProgramChange,
  TierAuditReviewTrigger,
  TierReviewTriggerMode,
} from "./tier-audit-diff";

/**
 * Builds `value_before` / `value_after` for the single UPDATE_PROGRAM audit row
 * (contract in `tier-audit-diff.ts`).
 *
 * `detail` is the raw `getTierProgramDetail` responseData — the state before editing.
 * `result` is what Step2 produced. Values on the "after" side mirror what
 * `saveHowItWorksAndTierLevels` actually sends, not just what the form shows.
 */
export function buildTierAuditDiff(
  detail: any,
  result: Step2Result,
  company: string,
): { before: TierAuditBefore; after: TierAuditDiff } {
  const apiLevels: any[] = detail?.tierLevels ?? [];

  const before: TierAuditBefore = {
    levels: apiLevels.map((t, i) => ({
      tierName: t.tierName,
      tierRank: t.tierRank ?? i + 1,
      benefitCount: (t.tierLevelBenefits ?? []).length,
    })),
    program: summarizeProgram(detail, company),
  };

  const levels: TierAuditLevelGroup[] = [];
  result.tiers.forEach((tier, i) => {
    const tierRank = i + 1;
    const original = apiLevels.find((t) => t.tierName === tier.tierKey);
    const changes = diffLevel(original, tier, tierRank, result, company);
    if (changes.length === 0) return;
    levels.push({
      tierName: tier.tierKey,
      tierRank,
      benefitCountBefore: (original?.tierLevelBenefits ?? []).length,
      benefitCountAfter: tier.benefits.length,
      changes,
    });
  });

  return { before, after: { levels, program: diffProgram(detail, result) } };
}

function diffLevel(
  original: any,
  tier: EditableTierBenefit,
  tierRank: number,
  result: Step2Result,
  company: string,
): TierAuditLevelChange[] {
  const changes: TierAuditLevelChange[] = [];

  const namesBefore = (original?.tierLevelBenefits ?? []).map((b: any) => (b.benefitName ?? "").trim());
  const namesAfter = tier.benefits.map((b) => (b.title ?? "").trim());
  namesAfter
    .filter((name) => !namesBefore.includes(name))
    .forEach((name) => changes.push({ type: "benefitAdd", name }));
  namesBefore
    .filter((name: string) => !namesAfter.includes(name))
    .forEach((name: string) => changes.push({ type: "benefitRemove", name }));

  // Both sides normalised the same way, so a stored null vs the 0 we now save is not a change.
  const percentFrom = toRebatePercent(original?.rebatePercent);
  const percentTo = toRebatePercent(tier.percent);
  if (percentFrom !== percentTo) changes.push({ type: "rebatePercent", from: percentFrom, to: percentTo });

  const titleFrom = toTextOrNull(original?.rebateTitle);
  const titleTo = toTextOrNull(tier.tierTitle);
  if (titleFrom !== titleTo) changes.push({ type: "rebateTitle", from: titleFrom, to: titleTo });

  const detailFrom = toTextOrNull(original?.rebateDetail);
  const detailTo = toTextOrNull(tier.titleDescription);
  if (detailFrom !== detailTo) changes.push({ type: "rebateDetail", from: detailFrom, to: detailTo });

  if ((original?.color ?? "").toLowerCase() !== (tier.color ?? "").toLowerCase()) {
    changes.push({ type: "color", value: tier.color ?? null });
  }

  if (result.cardImageOverridesFile?.[tier.tierKey]) changes.push({ type: "tierImageUrl", action: "set" });
  if (result.cardBackgroundOverridesFile?.[tier.tierKey]) changes.push({ type: "tierBgImageUrl", action: "set" });

  const triggerFrom: TierAuditReviewTrigger = {
    mode: (original?.reviewTriggerMode as TierReviewTriggerMode) ?? null,
    value: toNumberOrNull(original?.reviewTriggerValue),
    isNotAutoDemotable: Boolean(original?.isNotAutoDemotable),
  };
  const triggerTo: TierAuditReviewTrigger = {
    mode: triggerFrom.mode,
    // upsertTierLevel does not send reviewTriggerValue for rank 1, so it cannot change there.
    value: tierRank > 1 ? toNumberOrNull(tier.queueThresholdPercent) : triggerFrom.value,
    // Mirrors saveHowItWorksAndTierLevels: ICPL always sends false.
    isNotAutoDemotable: company !== "ICPL" ? Boolean(tier.preventAutoDemote) : false,
  };
  if (
    triggerFrom.value !== triggerTo.value ||
    triggerFrom.isNotAutoDemotable !== triggerTo.isNotAutoDemotable
  ) {
    changes.push({ type: "reviewTrigger", from: triggerFrom, to: triggerTo });
  }

  return changes;
}

function diffProgram(detail: any, result: Step2Result): TierAuditProgramChange[] {
  const changes: TierAuditProgramChange[] = [];

  if (result.bannerFile) changes.push({ type: "programImageUrl", action: "set" });

  const cardsBefore = (detail?.howItWorks ?? []).map((c: any) => [c.title, c.detail, c.imageUrl]);
  const cardsAfter = result.cards.map((c) => [c.title, c.description, c.File ? "__new_file__" : c.iconPath]);
  if (!sameRows(cardsBefore, cardsAfter)) {
    changes.push({ type: "howItWorks", ...titlesOf(result.cards.map((c) => c.title)) });
  }

  const faqsBefore = (detail?.faqs ?? []).map((f: any) => [f.title, f.detail]);
  const faqsAfter = result.faqs.map((f) => [f.question, f.answer]);
  if (!sameRows(faqsBefore, faqsAfter)) {
    changes.push({ type: "faqs", ...titlesOf(result.faqs.map((f) => f.question)) });
  }

  const rulesBefore = (detail?.termsAndConditions ?? []).map((r: any) => [r.title, r.detail]);
  const rulesAfter = result.rules.map((r) => [r.title, r.content]);
  if (!sameRows(rulesBefore, rulesAfter)) {
    changes.push({ type: "termsAndConditions", ...titlesOf(result.rules.map((r) => r.title)) });
  }

  return changes;
}

function summarizeProgram(detail: any, company: string): string {
  const year = detail?.year ?? "-";
  const faqCount = (detail?.faqs ?? []).length;
  const cardCount = (detail?.howItWorks ?? []).length;
  const ruleCount = (detail?.termsAndConditions ?? []).length;
  const banner = detail?.programImageUrl || detail?.programImageLink ? "มี banner" : "ไม่มี banner";
  return `ชุดสิทธิประโยชน์ ${company} ปี ${year} — FAQ ${faqCount} ข้อ, วิธีการทำงาน ${cardCount} ข้อ, กติกา ${ruleCount} ข้อ, ${banner}`;
}

function titlesOf(titles: string[]): { count: number; titles: string[] } {
  const trimmed = titles.map((t) => (t ?? "").trim());
  return { count: trimmed.length, titles: trimmed };
}

function sameRows(a: unknown[][], b: unknown[][]): boolean {
  const normalize = (rows: unknown[][]) =>
    JSON.stringify(rows.map((row) => row.map((v) => (typeof v === "string" ? v.trim() : v ?? null))));
  return normalize(a) === normalize(b);
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function toTextOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
